import { Injectable } from '@angular/core';
import { GlobalApiService } from '../global-api.service';
import { Observable } from 'rxjs';
import * as socketIO from 'socket.io-client';
import { Event } from 'src/app/models/side-nav.data';

@Injectable()
export class SocketService {
  private url = this.globalService.getSocketUrl();
  private socket: any;

  constructor(
    private globalService: GlobalApiService) {
  }
  initializeSocketIO() {
    this.socket = socketIO(this.url, { transports: ['websocket'] });
  }
  public sendMessage(message): void {
    this.socket.emit('new-message', message);
  }

  public notificationConnection(message): void {
    this.socket.emit('notificationConnection', message);
  }

  public checkPopUpStatus(branchId): void {
    this.socket.emit('checkPopUpStatus', branchId);
  }

  getMessages(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('reply', (data: any) =>
        observer.next(data));
    });
  }

  getAdds(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('new-add', (data: any) => {
        // console.log("NEW AD");
        observer.next(data)
      });
    });
  }

  getSocialPopUp(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('socialPopup', (data: any) => {
        // console.log("NEW AD");
        observer.next(data)
      });
    });
  }

  getNotifications(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('notification', (data: any) =>
        observer.next(data));
    });
  }

  // New-enquiry card (docs/plan-lead-popup.md) - sent only to Admin sockets by Services/leadPopupService.js
  getLeadOffers(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('leadOffer', (data: any) => observer.next(data));
    });
  }

  getLeadOfferClosed(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('leadOfferClosed', (data: any) => observer.next(data));
    });
  }

  getFeedback(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('gymFeedback', (data: any) =>
        observer.next(data));

    });
  }
  getBiometricSuggestion(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('biometricSuggestion', (data: any) =>
        observer.next(data));
    });
  }

  onsucess(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('success', (data: any) =>
        observer.next(data));

    });
  }

  getQrCode(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('wa-qrcode', (data: any) =>
        observer.next(data));
    });
  }
  sessionlogged(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('session-logged', (data: any) =>
        observer.next(data));
    });
  }
  sessionError(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('session-error', (data: any) =>
        observer.next(data));
    });
  }

  reLoginWhats(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('wa-login', (data: any) =>
        observer.next(data));
    });
  }

  public onEvent(event: Event): Observable<any> {
    return new Observable<Event>(observer => {
      this.socket.on(event, () => observer.next());
    });
  }

  // --- Emitters ---
  requestScreenShare(branchId: string, from: string) {
    this.socket.emit('screen-share-request', { branchId, from });
  }

  // Used by Centres -> Screen Share -> Active Login Sessions -> Continue. Unlike
  // requestScreenShare() above (which fans the request out to every socket currently registered
  // for a branch), this targets exactly the one staff session the admin picked from the active
  // sessions list, identified by its live socketId (see Branch.Model.js staffActiveSessions).
  // No separate 'from' is passed: the server uses targetSocketId itself as the session identity
  // (same role adminUserId played for the old Admin Registration flow), so it stays the single
  // consistent value used for the signaling room, remote-control validation, and the Shared
  // Screen viewer's own '?from=' query param - see screen-share-request-session in
  // Socket.Controller.js.
  requestScreenShareForSession(targetSocketId: string, branchId: string) {
    this.socket.emit('screen-share-request-session', { targetSocketId, branchId });
  }

  // Joins the caller's own socket into a screen-share signaling room, so relayed
  // webrtc-offer/webrtc-answer/ice-candidate events for that room reach it. Used by both the
  // branch (once it starts sharing) and the admin's Shared Screen viewer tab.
  joinScreenShareRoom(room: string) {
    this.socket.emit('screen-share-join', room);
  }

  sendOffer(target: string, from: string, room: string, offer: RTCSessionDescriptionInit) {
    this.socket.emit('webrtc-offer', { to: target, from, room, offer });
  }

  sendAnswer(target: string, from: string, room: string, answer: RTCSessionDescriptionInit) {
    this.socket.emit('webrtc-answer', { to: target, from, room, answer });
  }

  sendIceCandidate(target: string, from: string, room: string, candidate: RTCIceCandidate) {
    this.socket.emit('ice-candidate', { to: target, from, room, candidate });
  }

  // Listeners
  onScreenShareRequest(): Observable<{ from: string, room: string, message: string }> {
    return new Observable(observer => {
      this.socket.on('screen-share-request', (data) => observer.next(data));
    });
  }

  onOffer(): Observable<{ offer: RTCSessionDescriptionInit, sender: string, room: string }> {
    return new Observable(observer => {
      // The server relays this payload as { offer, sender, room } (see
      // Socket.Controller.js webrtc-offer handler) - it does NOT include a `from` key, so
      // reading data.from here always produced undefined. Read the key the server actually sends.
      this.socket.on('webrtc-offer', (data) => observer.next({
        offer: data.offer,
        sender: data.sender,
        room: data.room
      }));
    });
  }

  onAnswer(): Observable<{ answer: RTCSessionDescriptionInit, sender: string, room: string }> {
    return new Observable(observer => {
      // Same fix as onOffer() above - the server relays { answer, sender, room }, not { from }.
      this.socket.on('webrtc-answer', (data) => observer.next({
        answer: data.answer,
        sender: data.sender,
        room: data.room
      }));
    });
  }

  onIceCandidate(): Observable<{ candidate: RTCIceCandidate, sender: string, room: string }> {
    return new Observable(observer => {
      // Same fix as onOffer() above - the server relays { candidate, sender, room }, not { from }.
      this.socket.on('ice-candidate', (data) => observer.next({
        candidate: data.candidate,
        sender: data.sender,
        room: data.room
      }));
    });
  }

  onWebRTCEerror(): Observable<{ message: string, error: string }> {
    return new Observable(observer => {
      this.socket.on('webrtc-error', (data) => observer.next(data));
    });
  }

  // --- Remote Control (additive) -------------------------------------
  // These carry ONLY the low-frequency permission handshake
  // (request/allow/deny/pause/resume/stop) plus screen-share-end.
  // Actual mouse/keyboard/scroll control events travel peer-to-peer over
  // the WebRTC RTCDataChannel already opened on the existing screen-share
  // RTCPeerConnection (see SharedScreenComponent / SideNavComponent) and
  // never pass through Socket.IO - matching how this file already keeps
  // Socket.IO to signaling only for the video/ICE flow above.

  requestRemoteControl(room: string, branchId: string, from: string): void {
    this.socket.emit('remote-control-request', { room, branchId, from });
  }

  allowRemoteControl(room: string, branchId: string, from: string): void {
    this.socket.emit('remote-control-allow', { room, branchId, from });
  }

  denyRemoteControl(room: string, branchId: string, from: string): void {
    this.socket.emit('remote-control-deny', { room, branchId, from });
  }

  pauseRemoteControl(room: string, branchId: string, from: string): void {
    this.socket.emit('remote-control-pause', { room, branchId, from });
  }

  resumeRemoteControl(room: string, branchId: string, from: string): void {
    this.socket.emit('remote-control-resume', { room, branchId, from });
  }

  stopRemoteControl(room: string, branchId: string, from: string, reason?: string): void {
    this.socket.emit('remote-control-stop', { room, branchId, from, reason });
  }

  endScreenShare(room: string, branchId: string, from: string, reason?: string): void {
    this.socket.emit('screen-share-end', { room, branchId, from, reason });
  }

  onRemoteControlRequest(): Observable<{ room: string, from: string }> {
    return new Observable(observer => {
      this.socket.on('remote-control-request', (data) => observer.next(data));
    });
  }

  onRemoteControlAllow(): Observable<{ room: string, from: string }> {
    return new Observable(observer => {
      this.socket.on('remote-control-allow', (data) => observer.next(data));
    });
  }

  onRemoteControlDeny(): Observable<{ room: string, from: string }> {
    return new Observable(observer => {
      this.socket.on('remote-control-deny', (data) => observer.next(data));
    });
  }

  onRemoteControlPause(): Observable<{ room: string }> {
    return new Observable(observer => {
      this.socket.on('remote-control-pause', (data) => observer.next(data));
    });
  }

  onRemoteControlResume(): Observable<{ room: string }> {
    return new Observable(observer => {
      this.socket.on('remote-control-resume', (data) => observer.next(data));
    });
  }

  onRemoteControlStop(): Observable<{ room: string, reason: string }> {
    return new Observable(observer => {
      this.socket.on('remote-control-stop', (data) => observer.next(data));
    });
  }

  onScreenShareEnd(): Observable<{ room: string, reason: string }> {
    return new Observable(observer => {
      this.socket.on('screen-share-end', (data) => observer.next(data));
    });
  }

  onRemoteControlError(): Observable<{ message: string, error: string }> {
    return new Observable(observer => {
      this.socket.on('remote-control-error', (data) => observer.next(data));
    });
  }
}
